import { getConfig } from './storage'
import { syncKey, safeSetItem } from './supabase'

const BLING_TOKEN_SYNC_KEY = 'bling_oauth_tokens'
import { ADICIONAIS_BOOLEAN, ADICIONAIS_QUANTIDADE } from '../data/adicionais'

const API        = '/bling-api'
const delay      = (ms) => new Promise(r => setTimeout(r, ms))
const AUTH_URL   = 'https://www.bling.com.br/Api/v3/oauth/authorize'
// Proxy local evita bloqueio de CORS do browser
const TOKEN_URL  = '/bling-oauth/token'
const TOKENS_KEY = 'bling_oauth_tokens'

function getRedirectUri() {
  return `${window.location.origin}/bling/callback`
}

// ── Token storage ──────────────────────────────────────────────────────────────
function saveTokens(tokens) {
  safeSetItem(TOKENS_KEY, JSON.stringify(tokens))
  syncKey(BLING_TOKEN_SYNC_KEY)
}

export function getStoredTokens() {
  try { return JSON.parse(localStorage.getItem(TOKENS_KEY) || 'null') } catch { return null }
}

export function clearBlingTokens() {
  localStorage.removeItem(TOKENS_KEY)
  // Propaga o delete pro Supabase. Sem isso, outros devices recebem o
  // token velho no próximo syncFromSupabase e "reconectam" sozinhos
  // com credenciais já comprometidas/inválidas.
  syncKey(BLING_TOKEN_SYNC_KEY)
}

export function isBlingConnected() {
  return !!getStoredTokens()
}

// ── OAuth2 ─────────────────────────────────────────────────────────────────────
const OAUTH_STATE_KEY = 'bling_oauth_state'

export function getBlingAuthUrl(clientId) {
  // State aleatório, único por tentativa, gravado em sessionStorage
  // pra o callback validar. Sem isso, atacante que autoriza no Bling
  // dele consegue mandar um link com o próprio code pro admin e
  // substituir os tokens do Bling pelos dele (CSRF clássico de OAuth).
  const state = (crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now())
  try { sessionStorage.setItem(OAUTH_STATE_KEY, state) } catch {}
  const params = new URLSearchParams({
    response_type: 'code',
    client_id:     clientId,
    state,
  })
  return `${AUTH_URL}?${params}`
}

export function consumeExpectedBlingState() {
  try {
    const s = sessionStorage.getItem(OAUTH_STATE_KEY)
    sessionStorage.removeItem(OAUTH_STATE_KEY)
    return s
  } catch { return null }
}

export async function exchangeCodeForTokens(code, clientId, clientSecret) {
  const credentials = btoa(`${clientId}:${clientSecret}`)
  console.log('[Bling] trocando código por tokens...')
  const res = await fetch(TOKEN_URL, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/x-www-form-urlencoded',
      'Accept':        '1.0',
      'Authorization': `Basic ${credentials}`,
      'enable-jwt':    '1',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
    }).toString(),
  })

  const rawText = await res.text()
  console.log('[Bling] resposta token:', res.status, rawText)

  if (!res.ok) {
    let msg = `Erro ${res.status}`
    try { const e = JSON.parse(rawText); msg = e?.error_description || e?.message || msg } catch {}
    throw new Error(msg)
  }

  const data = JSON.parse(rawText)
  const tokens = {
    access_token:  data.access_token,
    refresh_token: data.refresh_token,
    expires_at:    Date.now() + (data.expires_in ?? 21600) * 1000,
    client_id:     clientId,
    client_secret: clientSecret,
  }
  console.log('[Bling] tokens salvos, access_token:', tokens.access_token?.slice(0, 20) + '...')
  saveTokens(tokens)
  return tokens
}

async function refreshAccessToken() {
  const t = getStoredTokens()
  if (!t?.refresh_token) throw new Error('Sem refresh token. Reconecte o Bling em Config.')

  const credentials = btoa(`${t.client_id}:${t.client_secret}`)
  const res = await fetch(TOKEN_URL, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/x-www-form-urlencoded',
      'Accept':        '1.0',
      'Authorization': `Basic ${credentials}`,
    },
    body: new URLSearchParams({
      grant_type:    'refresh_token',
      refresh_token: t.refresh_token,
    }).toString(),
  })


  if (!res.ok) {
    let msg = `Erro ${res.status}`
    try { const e = await res.json(); msg = e?.error_description || e?.message || msg } catch {}
    clearBlingTokens()
    throw new Error(`Sessão Bling expirada: ${msg}. Reconecte em Config.`)
  }

  const data = await res.json()
  const updated = {
    ...t,
    access_token:  data.access_token,
    refresh_token: data.refresh_token || t.refresh_token,
    expires_at:    Date.now() + (data.expires_in ?? 21600) * 1000,
  }
  saveTokens(updated)
  return updated.access_token
}

export async function getValidAccessToken() {
  const t = getStoredTokens()
  if (!t) throw new Error('Bling não conectado. Configure em Config → Bling ERP.')

  // Renova se expirar em menos de 5 minutos
  if (t.expires_at - Date.now() < 5 * 60 * 1000) {
    return await refreshAccessToken()
  }

  return t.access_token
}

// ── Descrição do móvel ──────────────────────────────────────────────────────────
function descricaoMovel(m) {
  let desc = m.modelo || 'Móvel'
  if (m.cor) desc += ` — ${m.cor}`
  const dimsBase = m.comprimento2
    ? [`${m.comprimento}×${m.comprimento2}`, m.altura, m.profundidade]
    : [m.comprimento, m.altura, m.profundidade]
  const dims = dimsBase.filter(Boolean)
  if (dims.length) desc += ` (${dims.join(' × ')} cm)`

  const extras = []
  if (m.adicionais?.puxadorTipo) {
    if (m.adicionais.puxadorTipo === 'Sem puxador') {
      extras.push('Sem puxador')
    } else {
      const cor = m.adicionais.puxadorCor ? ` ${m.adicionais.puxadorCor}` : ''
      const qtd = m.adicionais.puxadorQtd > 1 ? ` ×${m.adicionais.puxadorQtd}` : ''
      extras.push(`Puxador ${m.adicionais.puxadorTipo}${cor}${qtd}`)
    }
  }
  ADICIONAIS_BOOLEAN.forEach(({ key, label }) => {
    if (m.adicionais?.[key]) extras.push(label)
  })
  ADICIONAIS_QUANTIDADE.forEach(({ key, label }) => {
    const qtd = Number(m.adicionais?.[key] || 0)
    if (qtd > 0) extras.push(`${label}: ${qtd}`)
  })
  if (extras.length) desc += ` | ${extras.join(', ')}`
  if (m.observacoes) desc += ` — ${m.observacoes}`

  return desc
}

// ── Interpreta erros da API Bling ─────────────────────────────────────────────
function parseBlingError(rawText, fallback) {
  try {
    const e = JSON.parse(rawText)
    const tipo = (e?.error?.type || e?.error || '').toLowerCase()
    if (tipo === 'insufficient_scope') return null // sinaliza scope
    return e?.error?.message || e?.error_description || e?.message || fallback
  } catch {
    return fallback
  }
}

// ── Busca ou cria contato no Bling, retorna o id ──────────────────────────────
async function resolverContatoBling(token, cliente) {
  const doc = cliente.documento?.replace(/\D/g, '')

  // Bling API v3: endereço fica em endereco.geral
  const enderecoPayload = {
    geral: {
      endereco:    cliente.rua         || undefined,
      numero:      cliente.numero      || undefined,
      complemento: cliente.complemento || undefined,
      bairro:      cliente.bairro      || undefined,
      municipio:   cliente.cidade      || undefined,
      uf:          cliente.uf          || undefined,
      cep:         cliente.cep?.replace(/\D/g, '') || undefined,
    },
  }

  // 1. Tenta encontrar contato existente pelo CPF/CNPJ
  if (doc) {
    const res = await fetch(`${API}/contatos?numeroDocumento=${doc}&limite=1`, {
      headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
    })
    if (res.ok) {
      const json = await res.json()
      const lista = json?.data ?? json
      if (Array.isArray(lista) && lista.length > 0) {
        const id = lista[0].id
        console.log('[Bling] contato encontrado:', id)
        // Atualiza o endereço para refletir o pedido atual
        const putRes  = await fetch(`${API}/contatos/${id}`, {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            nome:            cliente.nome,
            tipo:            cliente.tipoDocumento === 'CNPJ' ? 'J' : 'F',
            situacao:        'A',
            numeroDocumento: doc || undefined,
            email:           cliente.email   || undefined,
            telefone:        cliente.contato || undefined,
            endereco:        enderecoPayload,
          }),
        })
        const putText = await putRes.text()
        console.log('[Bling] atualizar contato:', putRes.status, putText)
        console.log('[Bling] enderecoPayload enviado:', JSON.stringify(enderecoPayload))
        return id
      }
    }
  }

  // 2. Cria novo contato
  const novoContato = {
    nome:            cliente.nome,
    tipo:            cliente.tipoDocumento === 'CNPJ' ? 'J' : 'F',
    situacao:        'A',
    numeroDocumento: doc || undefined,
    email:           cliente.email   || undefined,
    telefone:        cliente.contato || undefined,
    endereco:        enderecoPayload,
  }
  const resCreate = await fetch(`${API}/contatos`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(novoContato),
  })
  const createText = await resCreate.text()
  console.log('[Bling] criar contato:', resCreate.status, createText)
  if (!resCreate.ok) {
    const parsed = parseBlingError(createText, `Erro ao criar contato: ${resCreate.status}`)
    throw new Error(parsed ?? 'Escopo insuficiente: ative "Contatos → Gerenciar contatos" no app Bling e reconecte')
  }
  const createData = JSON.parse(createText)
  const id = (createData?.data ?? createData)?.id
  console.log('[Bling] contato criado:', id)
  return id
}

// ── Abreviação de cor ──────────────────────────────────────────────────────────
const COR_MAP = {
  branco: 'BRC', branca: 'BRC',
  preto: 'PRT', preta: 'PRT',
  carvalho: 'CVL',
  nogueira: 'NGR',
  cinza: 'CNZ',
  amadeirado: 'AMD',
  grafite: 'GFT',
  nude: 'NDE',
  areia: 'ARE',
  natural: 'NAT',
  off: 'OFF',
  creme: 'CRM',
  marrom: 'MRM',
  azul: 'AZL',
  verde: 'VRD',
  rosa: 'RSA',
  vermelho: 'VRM',
}

function abreviarCor(cor) {
  if (!cor) return ''
  const chave = cor.toLowerCase().trim()
  for (const [k, v] of Object.entries(COR_MAP)) {
    if (chave.includes(k)) return v
  }
  return cor.replace(/\s+/g, '').slice(0, 3).toUpperCase()
}

// ── Nome abreviado do produto para o Bling ─────────────────────────────────────
function nomeProdutoBling(movel) {
  const partes = [movel.modelo || 'Móvel']

  const corAbrev = abreviarCor(movel.cor)
  if (corAbrev) partes.push(corAbrev)

  const comprStr = movel.comprimento2
    ? `${movel.comprimento}+${movel.comprimento2}`
    : movel.comprimento
  const dims = [comprStr, movel.altura, movel.profundidade].filter(Boolean)
  if (dims.length) partes.push(dims.join('×'))

  if (movel.adicionais?.puxadorTipo) {
    const puxCor = abreviarCor(movel.adicionais.puxadorCor)
    partes.push(`+PUX-${movel.adicionais.puxadorTipo}${puxCor ? `-${puxCor}` : ''}`)
  }

  return partes.join(' ')
}

// ── NCM (Nomenclatura Comum do Mercosul) do móvel ─────────────────────────────
// 94033000 = móveis de madeira de escritório
// 94036000 = outros móveis de madeira (default)
// Regex casa "home office", "escritório", "escrivaninha", "mesa dupla"
// (Mesa Dupla substituiu a antiga "Mesa Diretor" — é mesa de escritório).
const RE_ESCRITORIO = /home\s*office|escritorio|escrivaninha|mesa\s+dupla/i
function ncmDoMovel(movel) {
  const nome = (movel?.modelo || '')
    .toString()
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
  return RE_ESCRITORIO.test(nome) ? '94033000' : '94036000'
}

// ── Cria produto no Bling, retorna o id ───────────────────────────────────────
async function criarProdutoBling(token, movel) {
  const nomeFull = nomeProdutoBling(movel)

  const res = await fetch(`${API}/produtos`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({
      nome:      nomeFull,
      preco:     Number(movel.preco || 0),
      tipo:      'P',
      formato:   'S',
      situacao:  'A',
      unidade:   'UN',
      descricaoCurta: descricaoMovel(movel),
      // NCM obrigatório pra emissão fiscal. 94033000 escritório, 94036000 outros.
      tributacao: {
        ncm: ncmDoMovel(movel),
      },
    }),
  })
  const text = await res.text()
  console.log('[Bling] criar produto:', res.status, text)
  if (!res.ok) {
    const parsed = parseBlingError(text, `Erro ao criar produto: ${res.status}`)
    throw new Error(parsed ?? 'Escopo insuficiente: ative "Produtos → Gerenciar produtos" no app Bling e reconecte')
  }
  const data = JSON.parse(text)
  return (data?.data ?? data)?.id
}

// ── Criar pedido de venda no Bling ─────────────────────────────────────────────
export async function criarPedidoVendaBling(pedido) {
  const token      = await getValidAccessToken()
  const c          = pedido.cliente
  const contatoId  = await resolverContatoBling(token, c)

  // Cria um produto no Bling para cada móvel (sequencial para não estourar rate limit)
  const itens = []
  for (const m of pedido.moveis) {
    const produtoId = await criarProdutoBling(token, m)
    itens.push({
      produto:    produtoId ? { id: produtoId } : undefined,
      descricao:  descricaoMovel(m),
      quantidade: 1,
      valor:      Number(m.preco || 0),
      tipo:       'P',
      unidade:    'UN',
    })
    // Aguarda 400ms entre produtos para respeitar o limite de 3 req/s do Bling
    await delay(400)
  }

  const body = {
    numeroLoja:   pedido.numero,
    data:         pedido.dataVenda ?? pedido.criadoEm?.split('T')[0] ?? new Date().toISOString().split('T')[0],
    dataPrevista: pedido.prazo || undefined,
    contato: { id: contatoId },
    itens,
    transporte:  { fretePorConta: 3 }, // Transporte próprio por conta do remetente
    observacoes: pedido.observacoesGerais || '',
  }

  console.log('[Bling] enviando pedido, token:', token?.slice(0, 20) + '...')
  const res = await fetch(`${API}/pedidos/vendas`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Accept':        'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  const resText = await res.text()
  console.log('[Bling] resposta pedido:', res.status, resText)

  if (!res.ok) {
    const parsed = parseBlingError(resText, `Erro ${res.status}`)
    throw new Error(parsed ?? 'Escopo insuficiente: ative "Pedidos de venda → Gerenciar pedidos" no app Bling e reconecte')
  }

  const json = JSON.parse(resText)
  return json?.data ?? json
}

// ── Buscar detalhes de um pedido de venda ─────────────────────────────────────
export async function buscarPedidoBling(id) {
  const token = await getValidAccessToken()
  const res   = await fetch(`${API}/pedidos/vendas/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) return null
  const data = await res.json()
  return data.data ?? data
}

// ── Verifica se já existe pedido no Bling com esse numeroLoja ────────────────
// Retorna o pedido encontrado (id, numero, situação...) ou null.
// Usado pra prevenir criação duplicada em caso de timeout/retry.
export async function buscarPedidoBlingPorNumeroLoja(numero) {
  if (!numero) return null
  const token = await getValidAccessToken()
  // API v3: filtro por numeroLoja na listagem
  const url = `${API}/pedidos/vendas?numeroLoja=${encodeURIComponent(numero)}&limite=5`
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) return null
  const json = await res.json()
  const lista = json?.data || []
  // Bling devolve até 5 resultados. Procura match exato pra evitar prefix.
  return lista.find(p => String(p.numeroLoja || '') === String(numero)) || null
}

// ── Helper: fetch com retry em 429 ────────────────────────────────────────────
// Retorna { ok: true, data } ou { ok: false, status, reason } — nunca engole
// erros não-429. Antes, qualquer 401/500 virava `null` e o loop pulava o id
// silenciosamente: numa importação de 300 pedidos, 50 podiam faltar sem o
// usuário saber. Agora chama propaga `null` quando falha, mas o chamador
// consegue distinguir "não existe mais" (404) de "falha transitória" (500)
// e contar.
async function fetchComRetry(url) {
  for (let t = 0; t < 3; t++) {
    const token = await getValidAccessToken()
    const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    if (res.status === 429) {
      await delay(15000)
      continue
    }
    if (res.ok) {
      const data = await res.json()
      return { ok: true, data: data.data ?? data }
    }
    // 404 = pedido/contato não existe mais (ignora silencioso). Outros
    // (401, 403, 500, 502…) = falha real que o chamador contabiliza.
    return { ok: false, status: res.status, reason: res.status === 404 ? 'nao_encontrado' : 'erro' }
  }
  return { ok: false, status: 429, reason: 'rate_limit_persistente' }
}

// ── Buscar detalhes completos de pedidos ativos (para enriquecer cache) ────────
// Retorna { detalhes, falhas } — chamador decide o que fazer com falhas.
export async function importarDetalhesBlingAtivos(ids, onProgress) {
  const detalhes = []
  const falhas = []
  for (let i = 0; i < ids.length; i++) {
    const r = await fetchComRetry(`${API}/pedidos/vendas/${ids[i]}`)
    if (r.ok) detalhes.push({ id: String(ids[i]), data: r.data })
    else if (r.reason !== 'nao_encontrado') falhas.push({ id: String(ids[i]), status: r.status })
    onProgress?.(i + 1, ids.length)
    if (i < ids.length - 1) await delay(400)
  }
  // Mantém compat com chamadores antigos: `detalhes` continua sendo array
  // iterável. Falhas ficam acessíveis via .falhas pra UI mostrar.
  detalhes.falhas = falhas
  return detalhes
}

// ── Buscar detalhes completos de contatos (endereco.geral + celular) ──────────
export async function importarDetalhesContatosBling(ids, onProgress) {
  const detalhes = []
  const falhas = []
  for (let i = 0; i < ids.length; i++) {
    const r = await fetchComRetry(`${API}/contatos/${ids[i]}`)
    if (r.ok) detalhes.push(r.data)
    else if (r.reason !== 'nao_encontrado') falhas.push({ id: String(ids[i]), status: r.status })
    onProgress?.(i + 1, ids.length)
    if (i < ids.length - 1) await delay(400)
  }
  detalhes.falhas = falhas
  return detalhes
}

// ── Importar histórico de pedidos de venda do Bling ───────────────────────────
export async function importarPedidosBling(onProgress) {
  const token    = await getValidAccessToken()
  const pedidos  = []
  let pagina     = 1

  while (true) {
    const res = await fetch(`${API}/pedidos/vendas?pagina=${pagina}&limite=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const resText = await res.text()
    if (!res.ok) {
      const msg = parseBlingError(resText, `Erro ${res.status}`)
      throw new Error(msg ?? 'Escopo insuficiente: ative "Pedidos de venda → Visualizar" no app Bling e reconecte')
    }

    const data  = JSON.parse(resText)
    const items = data.data || []
    pedidos.push(...items)
    onProgress?.(pedidos.length)

    if (items.length < 100) break
    pagina++
    await delay(400)
  }

  return pedidos
}

// ── Importar todos os contatos ativos do Bling ────────────────────────────────
export async function importarContatosBling(onProgress) {
  const token    = await getValidAccessToken()
  const contatos = []
  let pagina     = 1

  while (true) {
    const res = await fetch(`${API}/contatos?pagina=${pagina}&limite=100&situacao=A`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const resText = await res.text()
    if (!res.ok) {
      const msg = parseBlingError(resText, `Erro ${res.status}`)
      throw new Error(msg ?? 'Escopo insuficiente: ative "Contatos → Visualizar" no app Bling e reconecte')
    }

    const data  = JSON.parse(resText)
    const items = data.data || []
    contatos.push(...items)
    onProgress?.(contatos.length)

    if (items.length < 100) break
    pagina++
    await delay(400)
  }

  return contatos
}
